import { makeAutoObservable } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";

import { peekQueryClient } from "@/queries/client";
import { workspaceTemplatesKey, workspaceTemplateVersionsKey } from "@/queries/workspace-templates";
import type { RootStore } from "./root.store";
import { queryCacheScope } from "./query-cache-scope";
import type { IWorkspaceTemplate, IWorkspaceTemplateCreateInput } from "@/types/workspace-template";

/**
 * The workspace template writers (create / delete / set default) and their
 * in-flight state for the management UI. The list and each template's versions
 * are queries (`app/queries/workspace-templates.ts`), read through
 * `hooks/use-workspace-templates.ts`; a confirmed write edits the cached list.
 *
 * List order is the server's: `my_last_applied_at DESC NULLS LAST, created_at
 * DESC` — the requesting user's most-recently-applied template leads. The
 * writers preserve that order verbatim (they do NOT re-sort): a create prepends (a brand-new template is the freshest action and is
 * reconciled to its true slot on the next fetch, and a re-capture of an existing
 * name replaces its row rather than duplicating it), a set-default patches the
 * row in place (default is not a sort key), and a delete filters in place.
 */
export class WorkspaceTemplatesStore {
  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  /**
   * Apply a confirmed write's list edit to the cached list
   * (`app/queries/workspace-templates.ts`), so a reader on
   * `useWorkspaceTemplates` shows it without a re-read. Does nothing before
   * the list was read.
   */
  private patchCachedList(
    slug: string,
    edit: (current: IWorkspaceTemplate[]) => IWorkspaceTemplate[],
  ): void {
    const scope = queryCacheScope(slug, "WorkspaceTemplatesStore");
    const client = scope ? peekQueryClient() : null;
    if (!scope || !client) return;
    client.setQueryData<IWorkspaceTemplate[]>(workspaceTemplatesKey(scope), (current) =>
      current ? edit(current) : current,
    );
  }

  /** Capture the CURRENT workspace into a new named template and cache it. */
  async create(
    slug: string,
    input: IWorkspaceTemplateCreateInput,
    source: AnalyticsSource = "page",
  ): Promise<IWorkspaceTemplate> {
    try {
      const created = await this.rootStore.workspaceTemplateService.create(slug, input);
      // Prepend — a just-created template is the freshest action; the next
      // read reconciles it to its true server-ordered slot. Capture is
      // idempotent per (creator, name), so re-capturing an existing name
      // returns that SAME template id updated in place (version bumped):
      // drop the stale row by id rather than prepending a duplicate of it.
      this.patchCachedList(slug, (current) => [
        created,
        ...current.filter((t) => t.id !== created.id),
      ]);
      track("template_created", {
        scope: "workspace",
        template_id: created.id,
        capture_scope: input.scope,
        source,
      });
      return created;
    } catch (err) {
      console.error(`Failed to create workspace template in "${slug}"`, { input, err });
      throw err;
    }
  }

  /** Delete a template and drop it from the cache. */
  async remove(slug: string, templateId: string, source: AnalyticsSource = "page"): Promise<void> {
    try {
      await this.rootStore.workspaceTemplateService.remove(slug, templateId);
      this.patchCachedList(slug, (current) => current.filter((t) => t.id !== templateId));
      const scope = queryCacheScope(slug, "WorkspaceTemplatesStore");
      if (scope) {
        peekQueryClient()?.removeQueries({
          queryKey: workspaceTemplateVersionsKey(scope, templateId),
          exact: true,
        });
      }
      track("template_deleted", { scope: "workspace", template_id: templateId, source });
    } catch (err) {
      console.error(`Failed to delete workspace template "${templateId}" in "${slug}"`, err);
      throw err;
    }
  }

  /**
   * Mark a template as the default new workspaces are seeded from. The backend
   * enforces a single default, so on success we patch the returned template into
   * the cached list AND clear `is_default` on every other cached template to mirror
   * that invariant locally. Default is not a sort key, so the row is patched in
   * place — the list keeps its server (applied-time) order.
   */
  async setDefault(
    slug: string,
    templateId: string,
    source: AnalyticsSource = "page",
  ): Promise<IWorkspaceTemplate> {
    try {
      const updated = await this.rootStore.workspaceTemplateService.setDefault(slug, templateId);
      this.patchCachedList(slug, (current) =>
        current.map((t) =>
          t.id === templateId ? updated : t.is_default ? { ...t, is_default: false } : t,
        ),
      );
      track("template_updated", {
        scope: "workspace",
        template_id: templateId,
        fields: ["default"],
        source,
      });
      return updated;
    } catch (err) {
      console.error(
        `Failed to set workspace template "${templateId}" as default in "${slug}"`,
        err,
      );
      throw err;
    }
  }
}
